/**
 * A rule's timing as structured pieces, anchor already resolved to its label —
 * the shape the resolver emits per entry and the rule cards build from a rule.
 */
export type FormulaSpec = {
  kind: string
  fixedTime: string | null
  offsetMinutes: number | null
  direction: string | null
  anchor: string | null
  roundStepMinutes: number
  roundDir: string
  /* Both clamps, because either can be the operation that decided the printed
     time. A formula stating only the cap explains a time the floor raised as
     though the offset had produced it. */
  notEarlierThan: string | null
  notLaterThan: string | null
  partsCount: number
  /** The anchor's resolved time — shown beside the formula in the logic view
   *  only (the cards omit it). The <Formula> itself does not render it. */
  anchorTime?: string | null
}

/** A stored "HH:mm" (24h) as a 12-hour clock time; the AM/PM recedes so the
 *  number leads. Renders the raw string if it isn't a time. */
function Time({ hhmm }: { hhmm: string | null | undefined }) {
  if (!hhmm) return null
  const [h, m] = hhmm.split(':').map(Number)
  if (Number.isNaN(h)) return <>{hhmm}</>
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return (
    <>
      {h12}:{String(m ?? 0).padStart(2, '0')}
      <span className="formula__ampm"> {period}</span>
    </>
  )
}

/**
 * The one rendering of a rule's timing — a readable statement with the anchor
 * highlighted, the rounding trailing as muted detail — shared by the rule cards
 * and the luach logic view so they never drift.
 */
export function Formula({ spec }: { spec: FormulaSpec }) {
  if (spec.kind === 'fixed')
    return (
      <span className="formula__fixed">
        {spec.fixedTime ? <Time hhmm={spec.fixedTime} /> : '—'}
      </span>
    )
  if (spec.kind === 'note')
    return <span className="formula__round">Text line</span>
  if (spec.kind === 'composite')
    return (
      <span className="formula__round">
        {spec.partsCount} value{spec.partsCount === 1 ? '' : 's'}
      </span>
    )
  if (spec.kind === 'sequence')
    return (
      <>
        <span className="formula__amt">straight after</span>{' '}
        <span className="formula__anchor">{spec.anchor}</span>
      </>
    )
  const round =
    spec.roundStepMinutes > 1
      ? `${spec.roundDir} to ${spec.roundStepMinutes} min`
      : 'to the minute'
  return (
    <>
      <span className="formula__amt">
        {spec.offsetMinutes ?? 0} min {spec.direction ?? ''}
      </span>{' '}
      <span className="formula__anchor">{spec.anchor}</span>
      {spec.notEarlierThan ? (
        <span className="formula__round">
          {' · never before '}
          <Time hhmm={spec.notEarlierThan} />
        </span>
      ) : null}
      {spec.notLaterThan ? (
        <span className="formula__round">
          {' · capped '}
          <Time hhmm={spec.notLaterThan} />
        </span>
      ) : null}
      <span className="formula__round"> · {round}</span>
    </>
  )
}
